import { IdSchema, PhoneNumberSchema } from './common';
import { t } from 'i18next';

import * as v from 'valibot';

export function adminInfoValueSchema<T extends string | null> (schema: v.GenericSchema<T>) {
	return v.pipe(
		v.nullable(v.string()),
		v.transform((val) => (val === '' || val === null ? null : val)),
		v.nullable(schema),
	);
}

export const AdminInfoValueTypeSchema = v.picklist([ 'text', 'number', 'email', 'phone', 'date', 'checkbox', 'list' ]);

export const AdminInfoValueRequestSchema = v.object({
	adminInfo: IdSchema,
	adminInfoListItemId: v.nullable(IdSchema),
	value: v.nullable(v.string()),
});

const BaseAdminInfoValueFormSchema = v.object({
	adminInfo: IdSchema,
	required: v.boolean(),
});

export const AdminInfoValueFormSchema = v.pipe(
	v.union([
		v.object({
			...BaseAdminInfoValueFormSchema.entries,
			type: v.literal('text'),
			adminInfoListItemId: v.null(),
			value: adminInfoValueSchema(v.string()),
		}),
		v.object({
			...BaseAdminInfoValueFormSchema.entries,
			type: v.literal('number'),
			adminInfoListItemId: v.null(),
			value: adminInfoValueSchema(v.pipe(
				v.string(),
				v.regex(/^-?\d+(\.\d+)?$/, t('Prosím zadejte platné číslo (v případě desetin použijte desetinnou tečku).')),
			)),
		}),
		v.object({
			...BaseAdminInfoValueFormSchema.entries,
			type: v.literal('email'),
			adminInfoListItemId: v.null(),
			value: adminInfoValueSchema(v.pipe(
				v.string(),
				v.email(t('Prosím zadejte platný e-mail.')),
			)),
		}),
		v.object({
			...BaseAdminInfoValueFormSchema.entries,
			type: v.literal('phone'),
			adminInfoListItemId: v.null(),
			value: adminInfoValueSchema(PhoneNumberSchema),
		}),
		v.object({
			...BaseAdminInfoValueFormSchema.entries,
			type: v.literal('date'),
			adminInfoListItemId: v.null(),
			value: adminInfoValueSchema(v.pipe(
				v.string(t('Prosím zadejte platné datum.')),
				v.isoDate(t('Prosím zadejte platné datum.')),
			)),
		}),
		v.object({
			...BaseAdminInfoValueFormSchema.entries,
			type: v.literal('checkbox'),
			adminInfoListItemId: v.null(),
			value: adminInfoValueSchema(v.string()),
		}),
		v.object({
			...BaseAdminInfoValueFormSchema.entries,
			type: v.literal('list'),
			adminInfoListItemId: v.nullable(IdSchema),
			value: v.null(),
		}),
	]),
	v.forward(
		v.partialCheck(
			[ [ 'required' ], [ 'type' ], [ 'value' ] ],
			(input) => {
				if (!input.required || input.type === 'checkbox' || input.type === 'list') { return true; }
				return input.value !== '' && input.value !== null;
			},
			t('Prosím nenechávejte toto pole prázdné.'),
		),
		[ 'value' ],
	),
	v.forward(
		v.partialCheck(
			[ [ 'required' ], [ 'type' ], [ 'value' ] ],
			(input) => {
				if (!input.required || input.type !== 'checkbox') { return true; }
				return input.value !== null && input.value !== '0';
			},
			t('Prosím nenechávejte toto pole nezvolené.'),
		),
		[ 'value' ],
	),
	v.forward(
		v.partialCheck(
			[ [ 'required' ], [ 'type' ], [ 'adminInfoListItemId' ] ],
			(input) => {
				if (!input.required || input.type !== 'list') { return true; }
				return input.adminInfoListItemId !== null;
			},
			t('Prosím vyberte platnou hodnotu z seznamu.'),
		),
		[ 'adminInfoListItemId' ],
	),
);

export type AdminInfoValueFormModel = v.InferInput<typeof AdminInfoValueFormSchema>;

